import React from "react";
import { Link } from "react-router-dom";
import { navigation, siteConfig } from "@/data/mock";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";

export const Footer = () => {
  return (
    <footer className="relative bg-gradient-to-b from-slate-900 to-slate-950 text-white overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
      <div className="absolute top-20 left-10 w-64 h-64 bg-amber-500/5 rounded-full blur-[100px]" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500/5 rounded-full blur-[120px]" />
      
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6 group">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center shadow-lg shadow-amber-500/20 group-hover:scale-105 transition-transform">
                <span className="text-white font-bold text-xl">P</span>
              </div>
              <span className="font-bold text-white text-xl tracking-tight">
                {siteConfig.companyName}
              </span>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md mb-6">
              Inventory-backed, multi-channel retail partner focused on sustainable growth and brand integrity. Based in Orlando, Florida.
            </p>
            
            {/* Contact info */}
            <div className="space-y-3">
              <a href={`mailto:${siteConfig.email}`} className="flex items-center gap-3 text-slate-400 hover:text-amber-400 transition-colors group">
                <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                  <Mail className="w-4 h-4" />
                </div>
                <span className="text-sm">{siteConfig.email}</span>
              </a>
              <a href={`tel:${siteConfig.phone}`} className="flex items-center gap-3 text-slate-400 hover:text-amber-400 transition-colors group">
                <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                  <Phone className="w-4 h-4" />
                </div>
                <span className="text-sm">{siteConfig.phone}</span>
              </a>
              <div className="flex items-center gap-3 text-slate-400">
                <div className="w-8 h-8 bg-white/5 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4" />
                </div>
                <span className="text-sm">Orlando, FL</span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-6">
              Navigation
            </h3>
            <ul className="space-y-3">
              {navigation.map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="group flex items-center text-slate-400 hover:text-white text-sm transition-colors duration-200"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-amber-500 mr-0 group-hover:mr-2 transition-all duration-200" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold text-white uppercase tracking-wider mb-6">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {[
                { name: 'Start Partnership', href: '/contact' },
                { name: 'Why It\'s Safe', href: '/why-safe' },
                { name: 'Our Team', href: '/team' },
                { name: 'Careers', href: '/careers' }
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    className="group flex items-center text-slate-400 hover:text-white text-sm transition-colors duration-200"
                  >
                    <span className="w-0 group-hover:w-2 h-px bg-amber-500 mr-0 group-hover:mr-2 transition-all duration-200" />
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 pt-8 border-t border-slate-800/50 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} {siteConfig.companyName}. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link
              to="/privacy"
              className="text-slate-500 hover:text-slate-300 text-sm transition-colors inline-flex items-center gap-1"
            >
              Privacy
              <ArrowUpRight className="w-3 h-3" />
            </Link>
            <Link
              to="/terms"
              className="text-slate-500 hover:text-slate-300 text-sm transition-colors inline-flex items-center gap-1"
            >
              Terms
              <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
